import axios from 'axios';
import { AuthStorageData } from '../models/models';
import { showGlobalSnackbar } from '../components/SnackbarProvider';
import { forceLogout } from '../utils/authActions';

const apiUrl = process.env.REACT_APP_API_KEY;

const api = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Lit les données auth depuis localStorage.
 * Clé : 'auth_data' (gérée par AuthSlice).
 */
function getAuthData(): AuthStorageData | null {
  try {
    const raw = localStorage.getItem('auth_data');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// ── Intercepteur de requête ───────────────────────────────────────────────────

api.interceptors.request.use(
  (config) => {
    const auth = getAuthData();
    const owner_id = auth?.user?.owner_id;
    const user_id  = auth?.user?.id;

    // Injection du token JWT dans le header Authorization
    if (auth?.token) {
      config.headers = config.headers ?? {};
      config.headers['Authorization'] = `Bearer ${auth.token}`;
    }

    // Injection département actif
    const deptRaw = localStorage.getItem('app_departement');
    const departement_id = deptRaw ? JSON.parse(deptRaw).id : null;

    if (config.method === 'get' || config.method === 'delete') {
      config.params = {
        ...config.params,
        ...(owner_id !== undefined && { owner_id }),
        ...(user_id  !== undefined && { user_id }),
        ...(departement_id && { departement_id }),
      };
    }

    if (config.method === 'post') {
      // Ne pas injecter sur les routes /auth (login, register)
      const url = config.url ?? '';
      // Envoi de fichier (FormData) : pas de fusion — l'étaler dans un objet
      // viderait le FormData. L'API lit alors owner_id / user_id dans le JWT.
      const isFichier = config.data instanceof FormData;
      if (!isFichier && !url.includes('/auth/login') && !url.includes('/auth/register')) {
        config.data = {
          ...config.data,
          ...(owner_id !== undefined && { owner_id }),
          ...(user_id  !== undefined && { user_id }),
          ...(departement_id && { departement_id }),
        };
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Intercepteur de réponse ───────────────────────────────────────────────────

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url    = error.config?.url ?? '';

    // Token expiré ou invalide → forcer la déconnexion centralisée
    // (même nettoyage qu'un logout manuel : reset Redux, vide localStorage,
    // coupe WebSocket, flag sessionStorage pour bannière LoginPage)
    if (status === 401 && !url.includes('/auth/login')) {
      forceLogout('token_expired');
    }

    // Accès refusé par scope ou permission → afficher le message backend
    if (status === 403) {
      const msg = error.response?.data?.message || 'Accès refusé.';
      showGlobalSnackbar(msg, 'error');
    }

    return Promise.reject(error);
  }
);

export default api;
